import React from 'react';
import { Layout } from 'antd';
import { useSelector } from 'react-redux';
import { PaymentOnboardingPage } from './PaymentOnboardingPage';
import { NotFoundPage } from '../../../app/pages/NotFoundPage';
import { t } from '../../../utils/i18n';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { Loading } from '../../../shared/components/Loading';
import { useAppSelector } from '../../../hooks/use-app-selector';
import { PaymentSetupPageV2 } from './PaymentSetupPageV2';

interface CurrentAccount {
  roles: Array<unknown>;
}

const headerContainerStyle = {
  alignItems: 'center',
  display: 'flex',
  margin: 'auto',
  marginBottom: '1.6rem',
  minHeight: '94px',
  padding: '1.6rem 2.4rem 0',
  width: '100%',
};

const headerTitleStyle = {
  fontSize: '25px',
  fontWeight: 700,
  margin: 0,
  padding: 0,
  flex: 1,
};

export function PaymentSettings() {
  // Comment to invalidate cache here
  const { isLoading: isLoadingSettings, merchantSettings } =
    useMerchantSettings();
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useSelector(
    (state: { currentAccount: CurrentAccount }) => state.currentAccount
  );
  const hasMultipleLocations = currentAccount?.roles.length > 1 || false;

  const paymentSettings = currentMerchant.paymentSettings;

  const paymentMethods = paymentSettings?.paymentMethods ?? [];

  const hasAnyActivePaymentMethod = paymentMethods.some(
    (paymentMethod) => paymentMethod.status === 'active'
  );

  const { featureToggles } = merchantSettings || {};
  const isFreeProduct = featureToggles?.free_product ?? false;
  const isOnlineBookingEnabled = featureToggles?.online_booking ?? false;
  const isOnlineBookingAndCustomerFeedbackEnabled =
    (featureToggles?.online_booking && featureToggles?.customer_feedback) ||
    false;
  const isOnlinePaymentsEnabled = featureToggles?.merchant_payment ?? false;

  if (isLoadingSettings) {
    return <Loading />;
  }

  if (!paymentSettings) {
    return <NotFoundPage />;
  }

  return (
    <Layout style={{ background: '#ffffff' }}>
      <Layout.Content>
        <shore-overlay-layout
          withoutPadding
          showContent
          style={{
            width: '100%',
            height: 'auto',
            display: 'flex',
            backgroundColor: '#fff',
            overflow: 'auto',
          }}
        >
          <header style={headerContainerStyle} slot="header">
            <h1 style={headerTitleStyle}>{t('application-settings.title')}</h1>
          </header>
          <aside slot="navigation">
            <shore-navigation>
              {isOnlineBookingAndCustomerFeedbackEnabled && (
                <shore-navigation-item
                  data-url
                  title={t('application-settings.nav.booking-widget')}
                  url="/application-settings/widgets"
                />
              )}
              {isOnlineBookingEnabled && !isFreeProduct && (
                <shore-navigation-item
                  data-url
                  title={t('application-settings.nav.booking-settings')}
                  url="/application-settings/booking"
                />
              )}
              {!isFreeProduct && (
                <shore-navigation-item
                  data-url
                  title={t('application-settings.nav.notifications')}
                  url="/application-settings/notification"
                />
              )}
              {isOnlinePaymentsEnabled && (
                <shore-navigation-item
                  data-url
                  isActive
                  title={t('application-settings.nav.payments')}
                />
              )}
              {isOnlineBookingEnabled && hasMultipleLocations && (
                <shore-navigation-item
                  data-url
                  title={t('application-settings.nav.location')}
                  url="/application-settings/branch"
                />
              )}
              {isOnlineBookingEnabled && !isFreeProduct && (
                <shore-navigation-item
                  data-url
                  title={t('application-settings.nav.integrations')}
                  url="/application-settings/integrations"
                />
              )}
              <shore-navigation-item
                data-url
                title={t('application-settings.nav.taxes')}
                url="/application-settings/services"
              />
              {
                // Note: This Button is disabled
                /* {isOnlineBookingEnabled && (
                    <shore-navigation-item
                      data-url
                      title={t('company-settings.nav.privacy-security')}
                      url="/application-settings/booking-button-setup"
                    />
                  )} */
              }
            </shore-navigation>
          </aside>
          <section slot="content">
            {hasAnyActivePaymentMethod ? (
              <PaymentSetupPageV2 />
            ) : (
              <PaymentOnboardingPage />
            )}
          </section>
        </shore-overlay-layout>
      </Layout.Content>
    </Layout>
  );
}
